/* eslint-disable import/prefer-default-export */
/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Berlin MCQ Adapter
 * Adapter to transform Berlin MCQ data to internal format
 * @file BerlinMCQAdapter.js
 * @author Satyaki Bose
 */

import Framework from '@greenville/framework';
import CommonUtils from '../../../common/utils/CommonUtils';

const getCorrectChoice = (choice, isMcqStatus = false, question = null, isStudent = false) => {
  // Show empty question with choices for instructor if the question is not attempted
  if (!isStudent && isMcqStatus && CommonUtils.isEmpty(question.mcqStatus || {})) {
    return 'No';
  }

  return choice?.isCorrect ? 'Yes' : 'No';
};

/**
 * Transform a single Berlin MCQ question to internal format
 */
const transformBerlinQuestion = (question, isMcqStatus = false, isStudent = false) => {
  const mcqStore = Framework.getStoreRegistry().getStore('mcqAssignments');
  // Transform choices to options
  const options = question?.choices?.map((choice, index) => ({
    id: index + 1,
    feedback: choice.feedback,
    distractor: choice.text,
    correctChoice: getCorrectChoice(choice, isMcqStatus, question, isStudent)
  }));

  let transformedQuestion = {
    questionType: 'multiple_choice',
    pageId: question.pageId,
    questionId: question.questionId,
    questionStem: question.questionStem,
    elementId: question.xpathSelector || question.xpath || '',
    relevantText: question.evidence?.text || [],
    evidence: question.evidence,
    options,
    topic: question.topicTitle || '',
    itemId: question.itemId || ''
  };

  // If mcqStatus exists, include it
  if (question.mcqStatus) {
    transformedQuestion = {
      ...transformedQuestion,
      assignmentId: question?.assignmentId,
      extraMaterialUsed: question?.mcqStatus?.extraMaterialUsed || false,
      extraMaterialUsedType: question?.mcqStatus?.extraMaterialUsedType || [],
      answerConfidence: question?.mcqStatus?.answerConfidence || '',
      answerAttempts: question?.mcqStatus?.answerAttempts?.map(attempt => ({
        attemptNumber: attempt.attemptNumber,
        answerNumber: mcqStore.convertAnswerKeyToNumber(attempt),
        attemptConfidence: attempt.attemptConfidence,
        isCorrect: attempt.isCorrect,
        serverUpdatedTimeStamp: attempt.serverUpdatedTimeStamp
      })) || []
    };
  }

  return transformedQuestion;
};

/**
* Transform Berlin API response for GET aet/books/{bookId}/mcqs
*/
const transformBerlinResponse = (berlinResponse) => {
  if (!berlinResponse || !berlinResponse.data || !berlinResponse.data.questions) {
    return {
      bookId: '',
      mcqQuestions: []
    };
  }
  const {
    bookId,
    questions
  } = berlinResponse.data;
  const mcqQuestions = questions.map(transformBerlinQuestion);

  return {
    bookId,
    mcqQuestions,
    requestId: berlinResponse.data.requestId,
    responseId: berlinResponse.data.responseId
  };
};

const extractQuestionsFromCourseAggregateMCQResponse = (response) => {
  const { assignments } = response;

  if (!assignments || !Array.isArray(assignments)) {
    return null;
  }

  const questions = assignments.flatMap((assignment) => {
    if (!assignment.assessment) {
      return null;
    }

    const { items } = assignment.assessment;

    if (!items || !Array.isArray(items)) {
      return null;
    }

    return items.map(item => ({
      ...item,
      ...item?.activity,
      assignmentId: assignment?.assignmentId,
      questionId: item?.activity?.activityId
    }));
  }).filter(Boolean);

  return questions;
};

/**
* Transform Berlin API response for GET aet/users/{userId}/courses/{courseId}/mcqs
*/
const transformCourseAggregateMCQResponse = (response) => {
  if (!response || !response.data) {
    return null;
  }

  const questions = extractQuestionsFromCourseAggregateMCQResponse(response.data);

  if (!questions || !Array.isArray(questions)) {
    return null;
  }

  const herobanner = Framework.getStoreRegistry().getStore('herobanner');
  const isStudent = CommonUtils.getIsStudent(herobanner.getHeroBook() || {});

  return questions.map(question => transformBerlinQuestion(question, true, isStudent));
};

export {
  transformBerlinResponse,
  transformBerlinQuestion,
  transformCourseAggregateMCQResponse
};
